import { CalculateServiceCostResult, Service } from '@anispi-reporting-system/types';
import { PrintOutlined } from '@mui/icons-material';
import ExcelJS from 'exceljs';
import React, { FunctionComponent, useEffect, useReducer } from 'react';
import { Button } from './Button';
import { labels } from '../lib/labels';

interface Props {
  companyName: string;
  companyNo: string;
  officeNo: string;
  service: Service;
  serviceCost: CalculateServiceCostResult['serviceCost'];
}

const DownloadResult: FunctionComponent<Props> = ({ companyName, companyNo, officeNo, service, serviceCost }) => {
  const [isClient, setIsClient] = useReducer(() => true, false);

  useEffect(() => {
    setIsClient();
  }, []);

  const handleClick = async () => {
    const workbook = new ExcelJS.Workbook();
    const fileName = `${companyNo}-${officeNo}-${labels.Service[service]}-${serviceCost?.useYearMonth}.xlsx`;

    workbook.addWorksheet('sheet1');
    const worksheet = workbook.getWorksheet('sheet1');
    worksheet.getCell('A1').font = { size: 18 };
    worksheet.getColumn(1).width = 20;
    worksheet.getColumn(2).width = 34;

    worksheet.getRow(1).values = ['加盟企業売上報告システム レベニューシェア、ロイヤリティ計算結果'];
    worksheet.getRow(3).values = ['企業名', companyName];
    worksheet.getRow(4).values = ['企業No', companyNo];
    worksheet.getRow(5).values = ['事業所番号', officeNo];
    worksheet.getRow(6).values = ['サービス', labels.Service[service]];
    worksheet.getRow(7).values = ['年月', serviceCost?.useYearMonth];

    if (serviceCost && 'totalMedicalAmount' in serviceCost && (service === 'HOMECARE-RS' || service === 'HOMECARE-RY')) {
      const { details } = serviceCost;
      const detailInsurance = details.find(({ uploadFileType }) => uploadFileType === 'INSURANCE');
      const detailUninsurance = details.find(({ uploadFileType }) => uploadFileType === 'UNINSURANCE');

      worksheet.getRow(9).values = [service === 'HOMECARE-RY' ? 'RY費' : 'RS費', `¥ ${serviceCost.serviceCost.toLocaleString()}`];

      worksheet.getRow(10).values = [
        '',
        `( ¥ ${detailInsurance?.totalMedicalAmount.toLocaleString()} + ¥ ${detailUninsurance?.totalMedicalAmount.toLocaleString()} ) * ${
          serviceCost.serviceRatio
        } = ¥ ${serviceCost.serviceCost.toLocaleString()}`,
      ];

      worksheet.getRow(12).values = ['医療費合計', `¥ ${serviceCost.totalMedicalAmount.toLocaleString()}`];
      worksheet.getRow(13).values = ['(保険内 医療費)', `¥ ${detailInsurance?.totalMedicalAmount.toLocaleString()}`];
      worksheet.getRow(14).values = ['(保険外 医療費)', `¥ ${detailUninsurance?.totalMedicalAmount.toLocaleString()}`];

      worksheet.getCell(9, 2).alignment = { horizontal: 'right' };
      worksheet.getCell(10, 2).alignment = { horizontal: 'right' };
      worksheet.getCell(12, 2).alignment = { horizontal: 'right' };
      worksheet.getCell(13, 2).alignment = { horizontal: 'right' };
      worksheet.getCell(14, 2).alignment = { horizontal: 'right' };
    } else if (
      serviceCost &&
      'totalTreatmentImprovementPrice' in serviceCost &&
      (service === 'RS' || service === 'RY' || service === 'RYSHORT')
    ) {
      worksheet.getColumn(3).width = 20;

      worksheet.getRow(9).values = [service === 'RS' ? 'RS費' : 'RY費', `¥ ${serviceCost.serviceCost.toLocaleString()}`];
      worksheet.getRow(10).values = [
        '',
        `( ¥ ${serviceCost.totalBillingAmount.toLocaleString()} - ¥ ${serviceCost.totalTreatmentImprovementPrice.toLocaleString()} ) * ${
          serviceCost.serviceRatio
        } = ¥ ${serviceCost.serviceCost.toLocaleString()}`,
      ];

      worksheet.getRow(12).values = ['請求額合計', `¥ ${serviceCost.totalBillingAmount.toLocaleString()}`];
      worksheet.getRow(13).values = ['処遇改善加算等額合計', `¥ ${serviceCost.totalTreatmentImprovementPrice.toLocaleString()}`];

      worksheet.getCell(9, 2).alignment = { horizontal: 'right' };
      worksheet.getCell(10, 2).alignment = { horizontal: 'right' };
      worksheet.getCell(12, 2).alignment = { horizontal: 'right' };
      worksheet.getCell(13, 2).alignment = { horizontal: 'right' };

      worksheet.getRow(15).values = ['(詳細)'];
      worksheet.getRow(16).values = ['請求額', '処遇改善加算等額', '(内訳)'];

      let offsetRowCount = 0;
      serviceCost.details.forEach((detail, index) => {
        worksheet.getRow(17 + index + offsetRowCount).values = [
          `¥ ${detail.billingAmount.toLocaleString()}`,
          `¥ ${detail.treatmentImprovementPrice.toLocaleString()}`,
        ];
        worksheet.getCell(17 + index + offsetRowCount, 1).alignment = { horizontal: 'right' };
        worksheet.getCell(17 + index + offsetRowCount, 2).alignment = { horizontal: 'right' };

        detail.serviceUnits.forEach((serviceUnit, serviceUnitIndex) => {
          worksheet.getCell(17 + index + serviceUnitIndex + offsetRowCount, 3).value = `(¥ ${serviceUnit.toLocaleString()} * ${
            detail.areaPrice
          })`;

          worksheet.getCell(17 + index + serviceUnitIndex + offsetRowCount, 3).alignment = { horizontal: 'right' };
        });

        if (detail.serviceUnits.length > 0) {
          offsetRowCount += detail.serviceUnits.length - 1;
        }
      });
    }

    const uint8Array = await workbook.xlsx.writeBuffer();
    const blob = new Blob([uint8Array], { type: 'application/octet-binary' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    a.remove();
  };

  if (!isClient) {
    return null;
  }

  return (
    <Button variant="outlined" onClick={handleClick} startIcon={<PrintOutlined />} sx={{ marginBottom: 1 }}>
      結果をダウンロード
    </Button>
  );
};

export { DownloadResult };
